(() => {
  "use strict";

  window.BYL = window.BYL || {};

  const CONFIG = window.BYL_CONFIG || {};
  const STOREFRONT_CUSTOMER = CONFIG.customer || {};

  const CLIENT_ID =
    String(CONFIG.customerAccountClientId || "").trim();

  const STORAGE = {
    accessToken: "byl_ca_access_token_v1",
    idToken: "byl_ca_id_token_v1",
    refreshToken: "byl_ca_refresh_token_v1",
    expiresAt: "byl_ca_expires_at_v1",

    pkceVerifier: "byl_ca_pkce_verifier_v1",
    authState: "byl_ca_auth_state_v1",
    returnTo: "byl_ca_return_to_v1"
  };

const METAFIELD_NAMESPACE = "boost_your_lunch";
const STUDENTS_KEY = "students";
const SCHOOLS_KEY = "schools";

  const ALLERGY_OPTIONS = [
    "Peanuts",
    "Tree nuts",
    "Dairy",
    "Egg",
    "Gluten",
    "Sesame",
    "Soy",
    "Fish/Shellfish"
  ];

  let openIdConfigPromise = null;
  let apiConfigPromise = null;
  let currentCustomerCache = null;


  /* =========================================================
     ERROR TYPE
     ========================================================= */

  class BYLCustomerError extends Error {
    constructor(message, options = {}) {
      super(message);

      this.name = "BYLCustomerError";
      this.code = options.code || "";
      this.status = options.status || 0;
      this.data = options.data || null;
    }
  }


  /* =========================================================
     BASIC HELPERS
     ========================================================= */

  function currentRedirectUri() {
    return `${window.location.origin}${window.location.pathname}`;
  }


  function randomString(byteLength = 32) {
    const bytes =
      new Uint8Array(byteLength);

    crypto.getRandomValues(bytes);

    return Array.from(bytes)
      .map(byte =>
        byte
          .toString(16)
          .padStart(2, "0")
      )
      .join("");
  }


  function base64UrlFromBuffer(buffer) {
    const bytes =
      new Uint8Array(buffer);

    let binary = "";

    for (const byte of bytes) {
      binary += String.fromCharCode(byte);
    }

    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");
  }


  async function createCodeChallenge(verifier) {
    const encoded =
      new TextEncoder().encode(verifier);

    const digest =
      await crypto.subtle.digest(
        "SHA-256",
        encoded
      );

    return base64UrlFromBuffer(digest);
  }


  function cleanText(value) {
    return String(value || "")
      .trim()
      .replace(/\s+/g, " ");
  }


  function safeJsonParse(value, fallback = null) {
    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return fallback;
    }

    if (typeof value === "object") {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }


  function createProfileId() {
    if (crypto.randomUUID) {
      return `child_${crypto.randomUUID()}`;
    }

    return `child_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2)}`;
  }


  /* =========================================================
     DISCOVERY
     ========================================================= */

  async function fetchDiscovery(path) {
    const response =
      await fetch(path, {
        headers: {
          Accept: "application/json"
        }
      });

    if (!response.ok) {
      throw new BYLCustomerError(
        "Shopify customer account configuration could not be loaded.",
        {
          code: "DISCOVERY_FAILED",
          status: response.status
        }
      );
    }

    return response.json();
  }


  function getOpenIdConfig() {
    if (!openIdConfigPromise) {
      openIdConfigPromise =
        fetchDiscovery(
          "/.well-known/openid-configuration"
        ).catch(error => {
          openIdConfigPromise = null;
          throw error;
        });
    }

    return openIdConfigPromise;
  }


  function getApiConfig() {
    if (!apiConfigPromise) {
      apiConfigPromise =
        fetchDiscovery(
          "/.well-known/customer-account-api"
        ).catch(error => {
          apiConfigPromise = null;
          throw error;
        });
    }

    return apiConfigPromise;
  }


  async function getDiscovery() {
    const [
      authentication,
      api
    ] = await Promise.all([
      getOpenIdConfig(),
      getApiConfig()
    ]);

    return {
      authentication,
      api
    };
  }


  /* =========================================================
     TOKEN STORAGE
     ========================================================= */

  function setSessionValue(key, value) {
    try {
      if (
        value === null ||
        value === undefined ||
        value === ""
      ) {
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(
          key,
          String(value)
        );
      }
    } catch (error) {
      console.warn(
        "BYL: Could not access session storage.",
        error
      );
    }
  }


  function getSessionValue(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }


  function clearTokens() {
    setSessionValue(
      STORAGE.accessToken,
      null
    );

    setSessionValue(
      STORAGE.idToken,
      null
    );

    setSessionValue(
      STORAGE.refreshToken,
      null
    );

    setSessionValue(
      STORAGE.expiresAt,
      null
    );

    currentCustomerCache = null;
  }


  function storeTokenResponse(data) {
    const accessToken =
      String(data?.access_token || "");

    if (!accessToken) {
      throw new BYLCustomerError(
        "Shopify did not return a customer access token.",
        {
          code: "MISSING_ACCESS_TOKEN",
          data
        }
      );
    }

    const expiresIn =
      Number(data.expires_in || 0);

    const expiresAt =
      expiresIn > 0
        ? Date.now() +
          expiresIn * 1000
        : 0;

    setSessionValue(
      STORAGE.accessToken,
      accessToken
    );

    setSessionValue(
      STORAGE.idToken,
      data.id_token || ""
    );

    setSessionValue(
      STORAGE.refreshToken,
      data.refresh_token || ""
    );

    setSessionValue(
      STORAGE.expiresAt,
      expiresAt || ""
    );

    return {
      accessToken,
      idToken:
        data.id_token || "",
      refreshToken:
        data.refresh_token || "",
      expiresAt
    };
  }


  function getStoredToken() {
    const accessToken =
      getSessionValue(
        STORAGE.accessToken
      );

    if (!accessToken) {
      return null;
    }

    const expiresAt =
      Number(
        getSessionValue(
          STORAGE.expiresAt
        ) || 0
      );

    // Keep a one-minute buffer.
    if (
      expiresAt &&
      Date.now() >=
        expiresAt - 60000
    ) {
      clearTokens();
      return null;
    }

    return {
      accessToken,

      idToken:
        getSessionValue(
          STORAGE.idToken
        ) || "",

      refreshToken:
        getSessionValue(
          STORAGE.refreshToken
        ) || "",

      expiresAt
    };
  }


  function getAccessToken() {
    return (
      getStoredToken()?.accessToken ||
      ""
    );
  }


  function isApiAuthenticated() {
    return Boolean(getAccessToken());
  }


  /* =========================================================
     PKCE AUTH
     ========================================================= */

  function assertClientId() {
    if (!CLIENT_ID) {
      throw new BYLCustomerError(
        "Customer Account API client ID is missing.",
        {
          code: "MISSING_CLIENT_ID"
        }
      );
    }
  }


  async function beginLogin(options = {}) {
    assertClientId();

    const config =
      await getOpenIdConfig();

    if (!config.authorization_endpoint) {
      throw new BYLCustomerError(
        "Shopify authorization endpoint is missing.",
        {
          code:
            "MISSING_AUTHORIZATION_ENDPOINT"
        }
      );
    }

    const verifier =
      randomString(48);

    const challenge =
      await createCodeChallenge(
        verifier
      );

    const state =
      randomString(24);

    const returnTo =
      String(
        options.returnTo ||
        window.location.href
      );

    setSessionValue(
      STORAGE.pkceVerifier,
      verifier
    );

    setSessionValue(
      STORAGE.authState,
      state
    );

    setSessionValue(
      STORAGE.returnTo,
      returnTo
    );

    const authUrl =
      new URL(
        config.authorization_endpoint
      );

    authUrl.searchParams.set(
      "scope",
      "openid email customer-account-api:full"
    );

    authUrl.searchParams.set(
      "client_id",
      CLIENT_ID
    );

    authUrl.searchParams.set(
      "response_type",
      "code"
    );

    authUrl.searchParams.set(
      "redirect_uri",
      currentRedirectUri()
    );

    authUrl.searchParams.set(
      "state",
      state
    );

    authUrl.searchParams.set(
      "code_challenge",
      challenge
    );

    authUrl.searchParams.set(
      "code_challenge_method",
      "S256"
    );

    if (options.prompt === "none") {
      authUrl.searchParams.set(
        "prompt",
        "none"
      );
    }

    window.location.assign(
      authUrl.toString()
    );
  }


  async function exchangeAuthorizationCode(
    code
  ) {
    assertClientId();

    const verifier =
      getSessionValue(
        STORAGE.pkceVerifier
      );

    if (!verifier) {
      throw new BYLCustomerError(
        "The customer login session could not be verified.",
        {
          code:
            "MISSING_PKCE_VERIFIER"
        }
      );
    }

    const config =
      await getOpenIdConfig();

    const tokenEndpoint =
      config.token_endpoint;

    if (!tokenEndpoint) {
      throw new BYLCustomerError(
        "Shopify token endpoint is missing.",
        {
          code:
            "MISSING_TOKEN_ENDPOINT"
        }
      );
    }

    const body =
      new URLSearchParams({
        grant_type:
          "authorization_code",

        client_id:
          CLIENT_ID,

        redirect_uri:
          currentRedirectUri(),

        code:
          String(code),

        code_verifier:
          verifier
      });

    let response;

    try {
      response =
        await fetch(
          tokenEndpoint,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/x-www-form-urlencoded",

              Accept:
                "application/json"
            },

            body
          }
        );
    } catch (error) {
      throw new BYLCustomerError(
        "Could not connect to Shopify customer accounts.",
        {
          code: "TOKEN_NETWORK_ERROR",
          data: error
        }
      );
    }

    let data = null;

    try {
      data = await response.json();
    } catch {
      data = null;
    }

    if (
      !response.ok ||
      !data?.access_token
    ) {
      throw new BYLCustomerError(
        data?.error_description ||
        data?.error ||
        "Shopify customer login could not be completed.",
        {
          code:
            "TOKEN_EXCHANGE_FAILED",

          status:
            response.status,

          data
        }
      );
    }

    setSessionValue(
      STORAGE.pkceVerifier,
      null
    );

    setSessionValue(
      STORAGE.authState,
      null
    );

    return storeTokenResponse(data);
  }


  function removeAuthParamsFromUrl() {
    const url =
      new URL(
        window.location.href
      );

    [
      "code",
      "state",
      "error",
      "error_description",
      "error_uri"
    ].forEach(key =>
      url.searchParams.delete(key)
    );

    window.history.replaceState(
      {},
      document.title,
      `${url.pathname}${
        url.search
      }${url.hash}`
    );
  }


  async function handleAuthCallback() {
    const url =
      new URL(
        window.location.href
      );

    const code =
      url.searchParams.get("code");

    const returnedState =
      url.searchParams.get("state");

    const oauthError =
      url.searchParams.get("error");

    const oauthDescription =
      url.searchParams.get(
        "error_description"
      );

    if (
      !code &&
      !oauthError
    ) {
      return {
        handled: false,
        authenticated:
          isApiAuthenticated()
      };
    }

    if (oauthError) {
      removeAuthParamsFromUrl();

      throw new BYLCustomerError(
        oauthDescription ||
        oauthError,
        {
          code:
            oauthError ===
            "login_required"
              ? "LOGIN_REQUIRED"
              : "AUTHORIZATION_ERROR"
        }
      );
    }

    const expectedState =
      getSessionValue(
        STORAGE.authState
      );

    if (
      !expectedState ||
      returnedState !== expectedState
    ) {
      removeAuthParamsFromUrl();

      throw new BYLCustomerError(
        "The customer login response could not be verified.",
        {
          code: "STATE_MISMATCH"
        }
      );
    }

    const token =
      await exchangeAuthorizationCode(
        code
      );

    const returnTo =
      getSessionValue(
        STORAGE.returnTo
      );

    setSessionValue(
      STORAGE.returnTo,
      null
    );

    removeAuthParamsFromUrl();

    return {
      handled: true,
      authenticated: true,
      token,
      returnTo
    };
  }


  /* =========================================================
     GRAPHQL
     ========================================================= */

  async function graphql(
    query,
    variables = {}
  ) {
    const accessToken =
      getAccessToken();

    if (!accessToken) {
      throw new BYLCustomerError(
        "Customer Account API authorization is required.",
        {
          code: "AUTH_REQUIRED"
        }
      );
    }

    const apiConfig =
      await getApiConfig();

    const endpoint =
      apiConfig.graphql_api;

    if (!endpoint) {
      throw new BYLCustomerError(
        "Shopify Customer Account GraphQL endpoint is missing.",
        {
          code:
            "MISSING_GRAPHQL_ENDPOINT"
        }
      );
    }

    let response;

    try {
      response =
        await fetch(
          endpoint,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              Accept:
                "application/json",

              // Shopify Customer Account API expects
              // the access token directly.
              Authorization:
                accessToken
            },

            body:
              JSON.stringify({
                query,
                variables
              })
          }
        );
    } catch (error) {
      throw new BYLCustomerError(
        "Could not connect to Shopify customer accounts.",
        {
          code:
            "GRAPHQL_NETWORK_ERROR",

          data:
            error
        }
      );
    }

    let payload = null;

    try {
      payload =
        await response.json();
    } catch {
      payload = null;
    }

    if (response.status === 401) {
      clearTokens();

      throw new BYLCustomerError(
        "Customer Account API authorization has expired.",
        {
          code:
            "AUTH_EXPIRED",

          status: 401
        }
      );
    }

    if (!response.ok) {
      throw new BYLCustomerError(
        payload?.errors?.[0]?.message ||
        "Shopify Customer Account API request failed.",
        {
          code:
            "GRAPHQL_HTTP_ERROR",

          status:
            response.status,

          data:
            payload
        }
      );
    }

    if (
      Array.isArray(payload?.errors) &&
      payload.errors.length
    ) {
      throw new BYLCustomerError(
        payload.errors[0].message ||
        "Shopify Customer Account API returned an error.",
        {
          code:
            "GRAPHQL_ERROR",

          data:
            payload.errors
        }
      );
    }

    return payload?.data || {};
  }


  /* =========================================================
     PROFILE NORMALIZATION
     ========================================================= */

  function normalizeAllergies(value) {
    const input =
      Array.isArray(value)
        ? value
        : [];

    const seen =
      new Set();

    const result = [];

    for (const allergy of input) {
      const clean =
        cleanText(allergy);

      if (!clean) continue;

      const official =
        ALLERGY_OPTIONS.find(
          option =>
            option.toLowerCase() ===
            clean.toLowerCase()
        );

      const finalValue =
        official || clean;

      const key =
        finalValue.toLowerCase();

      if (seen.has(key)) {
        continue;
      }

      seen.add(key);
      result.push(finalValue);
    }

    return result;
  }


  function normalizeProfile(profile = {}) {
    const schoolName =
      cleanText(
        profile.schoolName ||
        profile.school
      );

    const classroom =
      cleanText(
        profile.classroom ||
        profile.teacher ||
        profile.Teacher
      );

    const explicitType = cleanText(profile.memberType).toLowerCase();
    const legacyType = classroom.toLowerCase();
    const memberType = ["staff", "office"].includes(explicitType)
      ? explicitType
      : ["staff", "office"].includes(legacyType)
        ? legacyType
        : "student";

    return {
      id:
        cleanText(profile.id) ||
        createProfileId(),

      memberType,

      firstName:
        cleanText(
          profile.firstName
        ),

      lastName:
        cleanText(
          profile.lastName
        ),

      schoolId:
        cleanText(
          profile.schoolId
        ),

      schoolName,

      classroom,

      allergies:
        normalizeAllergies(
          profile.allergies
        ),

      otherAllergy:
        cleanText(
          profile.otherAllergy
        ),

      // Keep existing legacy notes so old
      // metafield data is never silently lost.
      notes:
        cleanText(
          profile.notes
        ),

      active:
        profile.active !== false
    };
  }


function normalizeStudentsPayload(
  raw
) {
  const parsed =
    safeJsonParse(
      raw,
      {}
    ) || {};

  const children =
    Array.isArray(parsed)
      ? parsed
      : Array.isArray(
          parsed.children
        )
        ? parsed.children
        : [];

  const historyDecisions =
    Array.isArray(
      parsed.historyDecisions
    )
      ? parsed.historyDecisions
      : [];

  return {
    children:
      children
        .map(normalizeProfile)
        .filter(
          child =>
            child.active !== false
        ),

    historyDecisions:
      historyDecisions
        .map(decision => ({
          historyKey:
            cleanText(
              decision?.historyKey
            ),

          action:
            cleanText(
              decision?.action
            ).toLowerCase(),

          profileId:
            cleanText(
              decision?.profileId
            )
        }))
        .filter(
          decision =>
            decision.historyKey &&
            [
              "merged",
              "hidden",
              "keep"
            ].includes(
              decision.action
            )
        ),

    savedAt:
      cleanText(
        parsed.savedAt
      )
  };
}


  function allergyText(profile) {
    const normalized =
      normalizeProfile(profile);

    const pieces = [
      ...normalized.allergies
    ];

    if (
      normalized.otherAllergy
    ) {
      pieces.push(
        normalized.otherAllergy
      );
    }

    if (
      normalized.notes &&
      !pieces.includes(
        normalized.notes
      )
    ) {
      pieces.push(
        normalized.notes
      );
    }

    return pieces
      .filter(Boolean)
      .join(", ");
  }
function normalizeSchoolsPayload(raw) {
  const parsed =
    safeJsonParse(
      raw,
      {}
    ) || {};

  const schools =
    Array.isArray(parsed.schools)
      ? parsed.schools
      : [];

  return {
    schools:
      schools
        .map(entry => ({
          schoolId:
            cleanText(
              entry?.schoolId
            ),

          schoolName:
            cleanText(
              entry?.schoolName
            ),

          accessToken:
            cleanText(
              entry?.accessToken
            ),

          verifiedAt:
            cleanText(
              entry?.verifiedAt
            )
        }))
        .filter(
          entry =>
            entry.schoolId &&
            entry.accessToken
        ),

    defaultSchoolId:
      cleanText(
        parsed.defaultSchoolId
      ),

    savedAt:
      cleanText(
        parsed.savedAt
      )
  };
}

  /* =========================================================
     CUSTOMER READ
     ========================================================= */

  const CUSTOMER_QUERY = `
    query BYLCurrentCustomer {
      customer {
        id
        firstName
        lastName
        displayName

        emailAddress {
          emailAddress
        }

        students:
          metafield(
            namespace: "boost_your_lunch"
            key: "students"
          ) {
            id
            namespace
            key
            type
            value
            jsonValue
            compareDigest
            updatedAt
          }
            schools:
  metafield(
    namespace: "boost_your_lunch"
    key: "schools"
  ) {
    id
    namespace
    key
    type
    value
    jsonValue
    compareDigest
    updatedAt
  }
      }
    }
  `;


  async function loadCurrentCustomer(
    options = {}
  ) {
    if (
      currentCustomerCache &&
      options.forceRefresh !== true
    ) {
      return currentCustomerCache;
    }

    const data =
      await graphql(
        CUSTOMER_QUERY
      );

    const customer =
      data.customer;

    if (!customer?.id) {
      throw new BYLCustomerError(
        "Shopify did not return the current customer.",
        {
          code:
            "MISSING_CUSTOMER"
        }
      );
    }

const rawStudents =
  customer.students?.jsonValue ??
  customer.students?.value ??
  null;

const rawSchools =
  customer.schools?.jsonValue ??
  customer.schools?.value ??
  null;

const normalized = {
      id:
        customer.id,

      firstName:
        cleanText(
          customer.firstName
        ),

      lastName:
        cleanText(
          customer.lastName
        ),

      displayName:
        cleanText(
          customer.displayName
        ),

      email:
        cleanText(
          customer.emailAddress
            ?.emailAddress
        ),

      students:
        normalizeStudentsPayload(
          rawStudents
        ),

      studentsMetafield:
  customer.students || null,

schools:
  normalizeSchoolsPayload(
    rawSchools
  ),

schoolsMetafield:
  customer.schools || null
    };

    currentCustomerCache =
      normalized;

    return normalized;
  }


  function getLiquidStudents() {
    return normalizeStudentsPayload(
      STOREFRONT_CUSTOMER.students
    );
  }


  function getInitialStudents() {
    return getLiquidStudents();
  }
function getInitialSchools() {
  return normalizeSchoolsPayload(
    STOREFRONT_CUSTOMER.schools
  );
}

  /* =========================================================
     CUSTOMER WRITE
     ========================================================= */

  const SAVE_STUDENTS_MUTATION = `
    mutation BYLSaveStudents(
      $metafields: [MetafieldsSetInput!]!
    ) {
      metafieldsSet(
        metafields: $metafields
      ) {
        metafields {
          id
          namespace
          key
          type
          value
          jsonValue
          compareDigest
          updatedAt
        }

        userErrors {
          field
          message
          code
        }
      }
    }
  `;


async function saveStudents(
  children
) {
  const customer =
    await loadCurrentCustomer();

  const normalizedChildren =
    (
      Array.isArray(children)
        ? children
        : []
    ).map(
      normalizeProfile
    );

  const historyDecisions =
    Array.isArray(
      customer.students
        ?.historyDecisions
    )
      ? customer.students
          .historyDecisions
      : [];

  const payload = {
    children:
      normalizedChildren,

    historyDecisions,

    savedAt:
      new Date()
        .toISOString()
  };

  const input = {
    ownerId:
      customer.id,

    namespace:
      METAFIELD_NAMESPACE,

    key:
      STUDENTS_KEY,

    type:
      "json",

    value:
      JSON.stringify(
        payload
      )
  };

  const data =
    await graphql(
      SAVE_STUDENTS_MUTATION,
      {
        metafields: [input]
      }
    );

  const result =
    data.metafieldsSet;

  const errors =
    result?.userErrors || [];

  if (errors.length) {
    throw new BYLCustomerError(
      errors
        .map(
          error =>
            error.message
        )
        .join(" "),
      {
        code:
          "METAFIELD_SAVE_ERROR",

        data:
          errors
      }
    );
  }

  const metafield =
    result?.metafields?.[0];

  currentCustomerCache = {
    ...customer,

    students:
      payload,

    studentsMetafield:
      metafield || null
  };

  return {
    children:
      normalizedChildren,

    historyDecisions,

    savedAt:
      payload.savedAt,

    metafield:
      metafield || null
  };
}

async function saveHistoryDecision(
  decision
) {
  const customer =
    await loadCurrentCustomer();

  const historyKey =
    cleanText(
      decision?.historyKey
    );

  const action =
    cleanText(
      decision?.action
    ).toLowerCase();

  const profileId =
    cleanText(
      decision?.profileId
    );

  if (
    !historyKey ||
    ![
      "merged",
      "hidden",
      "keep"
    ].includes(action)
  ) {
    throw new BYLCustomerError(
      "Profile history decision is invalid.",
      {
        code:
          "INVALID_HISTORY_DECISION"
      }
    );
  }

  const existing =
    Array.isArray(
      customer.students
        ?.historyDecisions
    )
      ? customer.students
          .historyDecisions
      : [];

  const next =
    [...existing];

  const normalizedDecision = {
    historyKey,
    action,
    profileId
  };

  const index =
    next.findIndex(
      item =>
        cleanText(
          item.historyKey
        ) === historyKey
    );

  if (index >= 0) {
    next[index] =
      normalizedDecision;
  } else {
    next.push(
      normalizedDecision
    );
  }

  const payload = {
    children:
      customer.students
        ?.children || [],

    historyDecisions:
      next,

    savedAt:
      new Date()
        .toISOString()
  };

  const input = {
    ownerId:
      customer.id,

    namespace:
      METAFIELD_NAMESPACE,

    key:
      STUDENTS_KEY,

    type:
      "json",

    value:
      JSON.stringify(
        payload
      )
  };

  const data =
    await graphql(
      SAVE_STUDENTS_MUTATION,
      {
        metafields: [input]
      }
    );

  const result =
    data.metafieldsSet;

  const errors =
    result?.userErrors || [];

  if (errors.length) {
    throw new BYLCustomerError(
      errors
        .map(
          error =>
            error.message
        )
        .join(" "),
      {
        code:
          "HISTORY_DECISION_SAVE_ERROR",

        data:
          errors
      }
    );
  }

  const metafield =
    result?.metafields?.[0];

  currentCustomerCache = {
    ...customer,

    students:
      payload,

    studentsMetafield:
      metafield || null
  };

  return {
    decision:
      normalizedDecision,

    students:
      payload,

    metafield:
      metafield || null
  };
}
async function saveSchoolAccess(
  school,
  options = {}
) {
  const customer =
    await loadCurrentCustomer();

  const schoolId =
    cleanText(
      school?.schoolId
    );

  const schoolName =
    cleanText(
      school?.schoolName
    );

  const accessToken =
    cleanText(
      school?.accessToken
    );

  if (
    !schoolId ||
    !accessToken
  ) {
    throw new BYLCustomerError(
      "School access information is incomplete.",
      {
        code:
          "INVALID_SCHOOL_ACCESS"
      }
    );
  }

  const existing =
    Array.isArray(
      customer.schools?.schools
    )
      ? customer.schools.schools
      : [];

  const savedSchool = {
    schoolId,
    schoolName,
    accessToken,

    verifiedAt:
      new Date().toISOString()
  };

  const next =
    [...existing];

  const index =
    next.findIndex(
      entry =>
        cleanText(
          entry.schoolId
        ) === schoolId
    );

  if (index >= 0) {
    next[index] =
      savedSchool;
  } else {
    next.push(
      savedSchool
    );
  }

  const payload = {
    schools:
      next,

    defaultSchoolId:
      options.makeDefault === false
        ? cleanText(
            customer.schools
              ?.defaultSchoolId
          ) || schoolId
        : schoolId,

    savedAt:
      new Date().toISOString()
  };

  const input = {
    ownerId:
      customer.id,

    namespace:
      METAFIELD_NAMESPACE,

    key:
      SCHOOLS_KEY,

    type:
      "json",

    value:
      JSON.stringify(payload)
  };

  const data =
    await graphql(
      SAVE_STUDENTS_MUTATION,
      {
        metafields: [input]
      }
    );

  const result =
    data.metafieldsSet;

  const errors =
    result?.userErrors || [];

  if (errors.length) {
    throw new BYLCustomerError(
      errors
        .map(error =>
          error.message
        )
        .join(" "),
      {
        code:
          "SCHOOL_METAFIELD_SAVE_ERROR",

        data:
          errors
      }
    );
  }

  const metafield =
    result?.metafields?.[0];

  currentCustomerCache = {
    ...customer,

    schools:
      payload,

    schoolsMetafield:
      metafield || null
  };

  return {
    ...payload,

    metafield:
      metafield || null
  };
}

async function setDefaultSchoolAccess(
  schoolId
) {
  const customer =
    await loadCurrentCustomer();

  const targetSchoolId =
    cleanText(
      schoolId
    );

  const existing =
    Array.isArray(
      customer.schools?.schools
    )
      ? customer.schools.schools
      : [];

  const hasSchool =
    existing.some(
      entry =>
        cleanText(
          entry.schoolId
        ) === targetSchoolId
    );

  if (
    !targetSchoolId ||
    !hasSchool
  ) {
    throw new BYLCustomerError(
      "That school is not saved to this account.",
      {
        code:
          "SCHOOL_DEFAULT_NOT_FOUND"
      }
    );
  }

  const payload = {
    schools:
      existing,

    defaultSchoolId:
      targetSchoolId,

    savedAt:
      new Date().toISOString()
  };

  const input = {
    ownerId:
      customer.id,

    namespace:
      METAFIELD_NAMESPACE,

    key:
      SCHOOLS_KEY,

    type:
      "json",

    value:
      JSON.stringify(payload)
  };

  const data =
    await graphql(
      SAVE_STUDENTS_MUTATION,
      {
        metafields: [input]
      }
    );

  const result =
    data.metafieldsSet;

  const errors =
    result?.userErrors || [];

  if (errors.length) {
    throw new BYLCustomerError(
      errors
        .map(error =>
          error.message
        )
        .join(" "),
      {
        code:
          "SCHOOL_DEFAULT_SAVE_ERROR",

        data:
          errors
      }
    );
  }

  const metafield =
    result?.metafields?.[0];

  currentCustomerCache = {
    ...customer,

    schools:
      payload,

    schoolsMetafield:
      metafield || null
  };

  return {
    ...payload,

    metafield:
      metafield || null
  };
}

async function removeSchoolAccess(
  schoolId
) {
  const customer =
    await loadCurrentCustomer();

  const targetSchoolId =
    cleanText(
      schoolId
    );

  if (!targetSchoolId) {
    return customer.schools;
  }

  const existing =
    Array.isArray(
      customer.schools?.schools
    )
      ? customer.schools.schools
      : [];

  const next =
    existing.filter(
      entry =>
        cleanText(
          entry.schoolId
        ) !== targetSchoolId
    );

  let defaultSchoolId =
    cleanText(
      customer.schools
        ?.defaultSchoolId
    );

  if (
    defaultSchoolId ===
    targetSchoolId
  ) {
    defaultSchoolId =
      next[0]?.schoolId || "";
  }

  const payload = {
    schools:
      next,

    defaultSchoolId,

    savedAt:
      new Date().toISOString()
  };

  const input = {
    ownerId:
      customer.id,

    namespace:
      METAFIELD_NAMESPACE,

    key:
      SCHOOLS_KEY,

    type:
      "json",

    value:
      JSON.stringify(payload)
  };

  const data =
    await graphql(
      SAVE_STUDENTS_MUTATION,
      {
        metafields: [input]
      }
    );

  const result =
    data.metafieldsSet;

  const errors =
    result?.userErrors || [];

  if (errors.length) {
    throw new BYLCustomerError(
      errors
        .map(error =>
          error.message
        )
        .join(" "),
      {
        code:
          "SCHOOL_METAFIELD_REMOVE_ERROR",

        data:
          errors
      }
    );
  }

  const metafield =
    result?.metafields?.[0];

  currentCustomerCache = {
    ...customer,

    schools:
      payload,

    schoolsMetafield:
      metafield || null
  };

  return {
    ...payload,

    metafield:
      metafield || null
  };
}

  async function upsertProfile(
    profile
  ) {
    const customer =
      await loadCurrentCustomer();

    const existing =
      customer.students
        ?.children || [];

    const normalized =
      normalizeProfile(profile);

    const index =
      existing.findIndex(
        child =>
          child.id ===
          normalized.id
      );

    const next =
      [...existing];

    if (index >= 0) {
      next[index] =
        normalized;
    } else {
      next.push(
        normalized
      );
    }

    const saved =
      await saveStudents(next);

    return {
      profile:
        normalized,

      students:
        saved
    };
  }


  async function deactivateProfile(
    profileId
  ) {
    const customer =
      await loadCurrentCustomer();

    const next =
      (
        customer.students
          ?.children || []
      ).map(profile => {
        if (
          profile.id !==
          String(profileId)
        ) {
          return profile;
        }

        return {
          ...profile,
          active: false
        };
      });

    return saveStudents(next);
  }


  /* =========================================================
     AUTH REQUIREMENT
     ========================================================= */

  async function requireAuthorization(
    options = {}
  ) {
    if (isApiAuthenticated()) {
      return {
        authenticated: true,
        redirected: false
      };
    }

    if (
      options.redirect === false
    ) {
      throw new BYLCustomerError(
        "Customer authorization is required.",
        {
          code: "AUTH_REQUIRED"
        }
      );
    }

    await beginLogin({
      returnTo:
        options.returnTo ||
        window.location.href
    });

    return {
      authenticated: false,
      redirected: true
    };
  }


  /* =========================================================
     PUBLIC API
     ========================================================= */

  BYL.customer = {
    BYLCustomerError,

config: {
  clientId:
    CLIENT_ID,

  redirectUri:
    currentRedirectUri(),

  namespace:
    METAFIELD_NAMESPACE,

  studentsKey:
    STUDENTS_KEY,

  schoolsKey:
    SCHOOLS_KEY
},

    allergies: {
      options:
        [...ALLERGY_OPTIONS],

      normalize:
        normalizeAllergies,

      toText:
        allergyText
    },

    profiles: {
      normalize:
        normalizeProfile,

      normalizePayload:
        normalizeStudentsPayload,

      initial:
        getInitialStudents,

      saveAll:
        saveStudents,

      upsert:
        upsertProfile,

      deactivate:
        deactivateProfile,

      historyDecision:
  saveHistoryDecision  
    },
schools: {
  normalizePayload:
    normalizeSchoolsPayload,

  initial:
    getInitialSchools,

  saveAccess:
    saveSchoolAccess,

  setDefault:
    setDefaultSchoolAccess,

  removeAccess:
    removeSchoolAccess
},

    discovery: {
      auth:
        getOpenIdConfig,

      api:
        getApiConfig,

      get:
        getDiscovery
    },

    auth: {
      begin:
        beginLogin,

      handleCallback:
        handleAuthCallback,

      require:
        requireAuthorization,

      isAuthenticated:
        isApiAuthenticated,

      getAccessToken,

      clear:
        clearTokens
    },

    api: {
      graphql,

      loadCustomer:
        loadCurrentCustomer
    }
  };

})();
